import { sumBy, isEmpty, isNil, snakeCase, toUpper } from 'lodash';
import { createSelector } from 'reselect';

import { getSelectedBank } from '../bankSelection/bankSelection.selectors';
import { getCostBasisMap } from '../costBasis/costBasis.selectors';
import {
  getAddressDetailsInArray,
  getBankDetailsInArray,
  getDirectDepositTimelines,
  getExpressDeliveryTimelines,
  getFundsByAddressLookup,
  getIsEFT,
  getMailACheckTimelines,
  getSelectedSellFundsList,
  getSellTakeHomeAmount,
  getSellTransactionDeductionTotal,
  getSellTransactionTotal,
  timeLine,
} from '../sellTransaction/sellTransaction.selectors';
import { getDeliveryMethod } from 'src/redux/modules/sellDeliveryMethod/sellDeliveryMethod.selectors';

import {
  COST_BASIS_DISPLAY_MAP,
  COLLEGE_DISTRIBUTE_PAYEE_OPTIONS,
  COLLEGE_DISTRIBUTE_PAYEE_OPTIONS_ENUM_MAP,
  DELIVERY_DIRECT_DEPOSIT,
  DELIVERY_IN_KIND,
  DELIVERY_ENUM_MAP,
  DELIVERY_MAIL_CHECK,
  DELIVERY_PRINT,
  REDEMPTION_SUBMISSION_DELIVERY_TYPE_ENUM,
  SELECTED_UNIT_ALPHA_MAP,
  SELECTED_UNIT_CASE_SENSITIVE_MAP,
  DELIVERY_MAIL_CHECK_TO_SCHOOL,
  SelectedUnitNum,
  UNIT,
  NON_RETIREMENT,
} from '../../../settings/settings';
import { getDisplayTradeDate } from '../funds/UserFunds/userFunds.selectors';
import {
  getUpdatedPaperless,
  getShowPaperlessOptIn,
} from '../user/Paperless/paperless.selectors';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import { agreementKey } from '../../../utils/funds/funds-utility';
import {
  isCollegeDistribute,
  isSell,
  isAppSell,
} from '../../../utils/transactions-util';
import { coalesce, currency } from '../../../utils/utils';
import { getRedemption, getTransactionsLookup } from './selectors';
export * from './selectors';

/**
 * @param getDeliveryMethod {Selector} {String DeliveryMethod}
 * @param getIsEFT {Selector} {Boolean} - Wire or ACH
 * @param getSelectedBank - {Selector} {BankObj} bank
 * @returns array of payload objects per fund should have all attributes to submit a trnx
 * EXAMPLE PAYLOAD
 * {
	"bank": {
		"bankAgreementID": "J9aJsnqgIOQghISwupwmXRth46lS21Dz"
	},
	"unit": {
			"code": "D",
			"enumerationID": 1,
			"enumerationName": "Dollars"
	},
	"delivery": {
			"enumerationID": 6,
			"enumerationName": "ElectronicTransferToBank"
	},
	"sourceAgreement": {
			"agreementID": "3958007"
	},
	"amount": 400
}

 */
export const getReddemTradeDates = createSelector(
  [getRedemption],
  (redemption) => {
    return redemption.redemptionTradeDates ?? {};
  }
);
export const getRedemptionPayload = createSelector(
  [
    getDeliveryMethod,
    getIsEFT,
    getSelectedBank,
    getSelectedSellFundsList,
    getShowPaperlessOptIn,
    getUpdatedPaperless,
  ],
  (
    deliveryMethod,
    isEFT,
    selectedBank,
    selectedFunds,
    showPaperless,
    updatedPaperless
  ) => {
    return selectedFunds.map((fund) => {
      const baseTransaction = {
        unit: {
          code:
            (fund.closeout && !fund.hasUncollectedShares) ||
            deliveryMethod === DELIVERY_IN_KIND
              ? SELECTED_UNIT_ALPHA_MAP[UNIT.ALL_SHARES]
              : fund.closeout && fund.hasUncollectedShares
              ? coalesce(SELECTED_UNIT_ALPHA_MAP[UNIT.SHARES], '')
              : coalesce(SELECTED_UNIT_ALPHA_MAP[fund.grossUnit], ''),
          enumerationID:
            (fund.closeout && !fund.hasUncollectedShares) ||
            deliveryMethod === DELIVERY_IN_KIND
              ? SelectedUnitNum[UNIT.ALL_SHARES]
              : fund.closeout && fund.hasUncollectedShares
              ? coalesce(SelectedUnitNum[UNIT.SHARES], '')
              : coalesce(SelectedUnitNum[fund.grossUnit], ''),
          enumerationName:
            (fund.closeout && !fund.hasUncollectedShares) ||
            deliveryMethod === DELIVERY_IN_KIND
              ? SELECTED_UNIT_CASE_SENSITIVE_MAP[UNIT.ALL_SHARES]
              : fund.closeout && fund.hasUncollectedShares
              ? coalesce(SELECTED_UNIT_CASE_SENSITIVE_MAP[UNIT.SHARES], '')
              : coalesce(SELECTED_UNIT_CASE_SENSITIVE_MAP[fund.grossUnit], ''), // this will eventually be a map of selectedFunds
        },
        delivery: {
          enumerationID: coalesce(DELIVERY_ENUM_MAP[deliveryMethod], ''), // confirm with Sagar what enumeration is and we will create a settings map
          enumerationName: coalesce(
            REDEMPTION_SUBMISSION_DELIVERY_TYPE_ENUM[deliveryMethod],
            ''
          ), // will need to be a mapping from sukanya's delivery types to either EFT / CHECK
        },
        deliveryPayee: {
          enumerationID:
            fund.payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.ACCOUNT_HOLDER
              ? COLLEGE_DISTRIBUTE_PAYEE_OPTIONS_ENUM_MAP.ACCOUNT_HOLDER.id
              : fund.payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY
              ? COLLEGE_DISTRIBUTE_PAYEE_OPTIONS_ENUM_MAP.BENEFICIARY.id
              : '',
          enumerationName:
            fund.payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.ACCOUNT_HOLDER
              ? COLLEGE_DISTRIBUTE_PAYEE_OPTIONS_ENUM_MAP.ACCOUNT_HOLDER.name
              : fund.payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY
              ? COLLEGE_DISTRIBUTE_PAYEE_OPTIONS_ENUM_MAP.BENEFICIARY.name
              : '',
        },
        sourceAgreement: {
          agreementID: coalesce(fund.agreementID, ''),
        },
        amount:
          fund.closeout || deliveryMethod === DELIVERY_IN_KIND
            ? coalesce(fund.availableShares, 1)
            : coalesce(fund.grossAmount, 0),
        iraWithholding:
          fund.federalWithholdingApplied && deliveryMethod !== DELIVERY_IN_KIND
            ? fund.federalWithholdingPercent
            : 0,
        paperlessConsent: updatedPaperless,
        currentPaperlessConsent: !showPaperless,
      };

      if (fund.closeout && !fund.hasUncollectedShares) {
        delete baseTransaction['amount'];
      }

      if (
        !isCollegeDistribute() ||
        deliveryMethod === DELIVERY_MAIL_CHECK_TO_SCHOOL
      ) {
        delete baseTransaction['deliveryPayee'];
      }

      if (isEFT && !isEmpty(selectedBank)) {
        return {
          ...baseTransaction,
          bank: {
            bankAgreementID: coalesce(selectedBank.bankAgreementID, ''),
          },
        };
      } else {
        return {
          ...baseTransaction,
        };
      }
    });
  }
);

/**
 * sellSubNotes that returns an array of notes of each particular fund.
 * @param {fund}  fund
 * @param {costBasisMethod} costBasisMethod
 * @returns array of object with fundNotes
 */
export function sellSubNotes(fund, costBasisMethod) {
  const sellConfirmationNoteArray = [];
  const { displayClassification, grossUnit, grossAmount } = fund;
  if (grossUnit === UNIT.PERCENT && grossAmount === 100) {
    sellConfirmationNoteArray.push({
      name: 'Note',
      value: 'Submitting this transaction will close the fund',
      nameBold: true,
      valueBold: false,
    });
  }

  if (displayClassification === NON_RETIREMENT) {
    sellConfirmationNoteArray.push({
      name: 'Cost Basis Method',
      value: costBasisMethod,
      nameBold: false,
      valueBold: true,
    });
  }
  // returning null if the array is empty.
  // As this is the way the backend need to print the data in confirmation page.

  return isEmpty(sellConfirmationNoteArray) ? null : sellConfirmationNoteArray;
}

/**
 * fundTransactionConfirmationDetails that returns fund details and the confirmation Number of a transaction
 * @param {funds}  funds
 * @param {transactionsLookup} transactionsLookup
 * @param {costBasisMap} costBasisMap
 * @returns object with fund details and the confirmation Number
 */
function fundTransactionConfirmationDetails(
  funds,
  transactionsLookup,
  costBasisMap,
  redemptionTradeDates
) {
  return funds?.map((fund) => {
    const {
      agreementID,
      agreementNumber,
      agreementType,
      displayClassification,
      formattedEntityNames,
      fundName,
      grossAmount,
      grossAmountDollars,
      grossUnit,
      productType,
      ticker,
    } = fund;
    const individualTransaction = transactionsLookup[agreementID] || {};
    const confirmNumber = !isEmpty(individualTransaction)
      ? isSell() || isAppSell()
        ? individualTransaction[0].confirmationNumber
        : individualTransaction[0].transaction.confirmationNumber
      : null;
    const fundAgreementKey = agreementKey(agreementID, agreementNumber);
    const costBasisMethod =
      !isNil(costBasisMap[fundAgreementKey]) &&
      costBasisMap[fundAgreementKey]['costBasisMethodType']
        ? COST_BASIS_DISPLAY_MAP[
            costBasisMap[fundAgreementKey]['costBasisMethodType']
          ]
        : 'N/A';
    return {
      tradeDate: formatUnixDate(redemptionTradeDates[agreementID]),
      accountType: `${displayClassification} / ${agreementType}`,
      fundName,
      ticker,
      accountNumber: agreementNumber,
      registrations: formattedEntityNames,
      productType: toUpper(snakeCase(productType)),
      subInfo: sellSubNotes(fund, costBasisMethod),
      amount: currency(grossAmountDollars),
      subAmount:
        grossUnit === UNIT.PERCENT
          ? [`${grossAmount}%`]
          : grossUnit === UNIT.SHARES
          ? [`${grossAmount} shares`]
          : null,
      confirmationNumber: confirmNumber,
    };
  });
}

export const getPrintData = createSelector(
  [
    getDeliveryMethod,
    getSelectedSellFundsList,
    getDisplayTradeDate,
    getBankDetailsInArray,
    getDirectDepositTimelines,
    getExpressDeliveryTimelines,
    getMailACheckTimelines,
    getFundsByAddressLookup,
    getTransactionsLookup,
    getCostBasisMap,
    getSellTakeHomeAmount,
    getSellTransactionDeductionTotal,
    getSellTransactionTotal,
    getReddemTradeDates,
  ],
  (
    deliveryMethod,
    selectedFundsList,
    displayTradeDate,
    bankDetailsInArray,
    directDepositTimelines,
    expressDeliveryTimelines,
    mailACheckTimelines,
    fundsByAddressLookup,
    transactionsLookup,
    costBasisMap,
    sellTakeHomeAmount,
    sellTransactionDeductionTotal,
    sellTransactionTotal,
    redemptionTradeDates
  ) => {
    if (deliveryMethod === DELIVERY_IN_KIND) {
      selectedFundsList = selectedFundsList.map((fund) => {
        return {
          ...fund,
          grossAmountDollars: fund.balanceAsOf,
          grossUnit: 'PERCENT',
          grossAmount: 100,
          grossPercentShares: 100,
        };
      });
    }
    const deliveryMethodName = DELIVERY_PRINT[deliveryMethod];
    const estimatedDeliveryTimeline =
      deliveryMethod === DELIVERY_DIRECT_DEPOSIT
        ? isAppSell()
          ? '2-3 days from trade date'
          : timeLine(directDepositTimelines)
        : deliveryMethod === DELIVERY_MAIL_CHECK
        ? isAppSell()
          ? '7-10 days from trade date'
          : timeLine(mailACheckTimelines)
        : deliveryMethod === DELIVERY_IN_KIND
        ? ''
        : isAppSell()
        ? '1-2 days from trade date'
        : timeLine(expressDeliveryTimelines);

    const bankTransactionLine = [
      {
        deliveryMethodName,
        deliveryMethodLines: bankDetailsInArray,
        estimatedDeliveryTimeline,
        transactionLines: fundTransactionConfirmationDetails(
          selectedFundsList,
          transactionsLookup,
          costBasisMap,
          redemptionTradeDates
        ),
      },
    ];
    const checkTransactionLine = Object.keys(fundsByAddressLookup).map(
      (address) => {
        const funds = fundsByAddressLookup[address].funds;
        const firstFund = funds && funds[0];
        return {
          deliveryMethodName,
          deliveryMethodLines: getAddressDetailsInArray(firstFund),
          estimatedDeliveryTimeline,
          transactionLines: fundTransactionConfirmationDetails(
            funds,
            transactionsLookup,
            costBasisMap,
            redemptionTradeDates
          ),
        };
      }
    );
    const inKindAmount = sumBy(selectedFundsList, (fund) => fund.balanceAsOf);
    return {
      tradeDate: !isAppSell() ? formatUnixDate(displayTradeDate) : null,
      deliveryMethodData:
        deliveryMethod === DELIVERY_MAIL_CHECK
          ? checkTransactionLine
          : bankTransactionLine,
      subTotal:
        deliveryMethod === DELIVERY_IN_KIND
          ? currency(inKindAmount)
          : currency(sellTransactionTotal),
      deductions: `-${currency(sellTransactionDeductionTotal)}`,
      takeHomeAmount:
        deliveryMethod === DELIVERY_IN_KIND
          ? currency(inKindAmount)
          : currency(sellTakeHomeAmount),
    };
  }
);
